import { Formik, FormikHelpers } from 'formik'
import { useRouter } from 'next/router'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { MdCheck } from 'react-icons/md'

import { useFocusActionState } from 'src/providers/FocusActionProvider'
import { GoogleMapsProvider } from 'src/providers/GoogleMapsProvider'
import { ModalType, useModal } from 'src/providers/ModalProvider'
import { NotificationType, notify } from 'src/providers/NotificationProvider'

import { DefinitionListItem } from 'src/components/atoms/DefinitionListItem'
import { UploadProgress } from 'src/components/atoms/UploadProgress'
import { ProjectForm } from 'src/components/organisms/forms/ProjectForm'
import {
  parseCreateProject,
  parseUpdateProject
} from 'src/components/organisms/forms/ProjectForm/helper'
import { ProjectFormValues } from 'src/components/organisms/forms/ProjectForm/types'
import { validationSchema } from 'src/components/organisms/forms/ProjectForm/validationSchema'

import {
  useUpdateProjectMutation,
  namedOperations,
  AssetType,
  useDeleteProjectAssetMutation,
  useCreateProjectHeroMutation,
  ProjectAssetType,
  useCreateProjectMutation,
  useCreateProjectAssetMutation
} from 'src/config/generated/graphql'
import {
  adminListProjectsPath,
  adminViewProjectPath,
  createProjectSuccessPath,
  listProjectsPath,
  viewProjectDetailPath
} from 'src/config/paths'
import { useRollback } from 'src/hooks/useRollback'
import { useUploadProgress } from 'src/hooks/useUploadProgress'
import { S3Asset } from 'src/types/form'
import { ProjectType } from 'src/types/project'
import { buildAssetsDiff } from 'src/utils/assets'
import {
  FileToUpload,
  UploadAssetType,
  uploadFileToS3,
  uploadFilesToS3
} from 'src/utils/fileUpload'

const DEFAULT_BANNER = 'assets/img/default-profile.png'

export interface EditProjectProps {
  project?: ProjectType
  projectPhotosInS3: (S3Asset | undefined)[]
  isAdmin?: boolean
}

export const EditProject = ({
  project,
  projectPhotosInS3,
  isAdmin = false
}: EditProjectProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const router = useRouter()
  const { projectId } = router.query as {
    projectId: string
  }

  const { setFocussedHeader } = useFocusActionState()

  const { openModal } = useModal()
  const [createProject] = useCreateProjectMutation({
    refetchQueries: [
      namedOperations.Query.getUserAndMemberships,
      namedOperations.Query.getProject,
      namedOperations.Query.getAdminProject
    ]
  })

  const [createProjectHero] = useCreateProjectHeroMutation({
    refetchQueries: [
      namedOperations.Query.getProject,
      namedOperations.Query.getAdminProject
    ]
  })

  const [createProjectAsset] = useCreateProjectAssetMutation({
    refetchQueries: [
      namedOperations.Query.getProject,
      namedOperations.Query.getAdminProject
    ]
  })

  const [deleteProjectAsset] = useDeleteProjectAssetMutation({
    refetchQueries: [
      namedOperations.Query.getProject,
      namedOperations.Query.getAdminProject
    ]
  })

  const rollbackProjectAsset = useRollback(deleteProjectAsset)

  const [updateProject] = useUpdateProjectMutation({
    refetchQueries: [
      namedOperations.Query.getProject,
      namedOperations.Query.getAdminProject
    ]
  })

  const initialValues: ProjectFormValues = useMemo(() => {
    const websiteLinks =
      project?.WebsiteLinks && project.WebsiteLinks.length > 0
        ? project.WebsiteLinks
        : ['']

    return {
      Id: project?.Id,
      Area: project?.Area || '< 2 Ha',
      Description: project?.Description || '',
      District: project?.District?.Id || undefined,
      Geo: project?.Geo
        ? JSON.parse(project.Geo)
        : { lat: 0, lng: 0, place_id: '' },
      Hero: project?.Hero || DEFAULT_BANNER,
      HeroFile: [],
      IsPublic: false,
      LandUse: project?.LandUse || [],
      LandUseOther: project?.LandUseOther || '',
      Name: project?.Name || '',
      NumberOfProperties: project?.NumberOfProperties || '1',
      OpenFrom: project?.OpenFrom || '',
      OpenTo: project?.OpenTo || '',
      OtherLandArea: project?.OtherLandArea || 'Not at the moment',
      PlantingTimeframes: project?.PlantingTimeframes || 'Up to 1 yr',
      ProjectAssets: projectPhotosInS3 || null,
      Purpose: project?.Purpose || [],
      Region: project?.Region?.Id || undefined,
      WebsiteLinks: websiteLinks,
      WorkflowState: project?.WorkflowState || 'Draft'
    }
  }, [project, projectPhotosInS3])

  const { handleUploadProgress, isUploading, progress } = useUploadProgress()

  const createAndDeleteAssets = useCallback(
    async ({ id, values }: { id: string; values: ProjectFormValues }) => {
      const projectAssetsToDelete = buildAssetsDiff(
        projectPhotosInS3 as S3Asset[],
        values.ProjectAssets as S3Asset[]
      )

      if (projectAssetsToDelete.length) {
        projectAssetsToDelete.forEach(async atd => {
          if ((atd as S3Asset).Id) {
            await deleteProjectAsset({
              variables: {
                id: (atd as S3Asset).Id
              }
            })
          }
        })
      }
      // Creating project assets
      const projectAssetsToCreate = buildAssetsDiff(
        values.ProjectAssets as S3Asset[],
        projectPhotosInS3 as S3Asset[]
      ).map(pa => ({
        file: pa,
        type: AssetType.Image
      }))

      const filesToTrack: FileToUpload[] = [...projectAssetsToCreate]
      if (values.HeroFile?.[0]) {
        filesToTrack.unshift({
          file: values.HeroFile[0] as S3Asset,
          type: ProjectAssetType.ProjectHero
        })
      }

      const { setFileProgress } = handleUploadProgress(filesToTrack)

      if (values.HeroFile?.[0]) {
        const file = values.HeroFile[0] as S3Asset
        await uploadFileToS3(
          createProjectHero,
          { file, type: ProjectAssetType.ProjectHero },
          UploadAssetType.ProjectBanner,
          id,
          undefined,
          setFileProgress
        )
      }

      if (projectAssetsToCreate.length) {
        await uploadFilesToS3(
          createProjectAsset,
          projectAssetsToCreate,
          UploadAssetType.Project,
          id,
          rollbackProjectAsset,
          setFileProgress
        )
      }
    },
    [
      createProjectAsset,
      createProjectHero,
      deleteProjectAsset,
      handleUploadProgress,
      projectPhotosInS3,
      rollbackProjectAsset
    ]
  )

  const createAssets = useCallback(
    async (id: string, values: ProjectFormValues) => {
      await createAndDeleteAssets({
        id,
        values
      })
      setIsSubmitting(false)
    },
    [createAndDeleteAssets]
  )

  const handleSubmit = useCallback(
    async (
      formValues: ProjectFormValues,
      actions?: FormikHelpers<ProjectFormValues>,
      isDraft: boolean = false
    ) => {
      try {
        setIsSubmitting(true)
        if (!project) {
          const input = parseCreateProject(formValues, isDraft)

          await createProject({
            variables: { input },
            onCompleted: async res => {
              await createAssets(res.create_project?.Id!, formValues)
              if (isDraft) {
                if (isAdmin) {
                  router.push(
                    adminViewProjectPath({
                      routeParams: { projectId: res.create_project?.Id! }
                    })
                  )
                  return
                }
                router.push(
                  viewProjectDetailPath({
                    routeParams: {
                      projectId: res.create_project?.Id!
                    }
                  })
                )
                return
              }
              router.push(
                isAdmin
                  ? adminViewProjectPath({
                      routeParams: { projectId }
                    })
                  : createProjectSuccessPath({
                      routeParams: { projectId: res.create_project?.Id! }
                    })
              )
            }
          })
        } else {
          await updateProject({
            variables: {
              input: parseUpdateProject(projectId, formValues, isDraft)
            },
            onCompleted: async () => {
              await createAssets(projectId, formValues)

              if (isDraft) {
                if (isAdmin) {
                  router.push(
                    adminViewProjectPath({
                      routeParams: {
                        projectId
                      }
                    })
                  )
                  return
                }
                router.push(
                  viewProjectDetailPath({
                    routeParams: {
                      projectId
                    }
                  })
                )
                return
              }
              router.push(
                isAdmin
                  ? adminViewProjectPath({
                      routeParams: {
                        projectId
                      }
                    })
                  : createProjectSuccessPath({
                      routeParams: {
                        projectId
                      }
                    })
              )
            }
          })
        }
      } catch (error: any) {
        notify({ message: error.message, type: NotificationType.error })
        setIsSubmitting(false)
      }
    },
    [
      createAssets,
      createProject,
      isAdmin,
      project,
      projectId,
      router,
      updateProject
    ]
  )

  const handleSaveAsDraft = useCallback(
    async (formValues: ProjectFormValues) => {
      await handleSubmit(formValues, undefined, true)
    },
    [handleSubmit]
  )

  const handleDiscard = () => {
    notify({
      icon: MdCheck,
      message: 'Your project information was not saved.',
      type: NotificationType.success
    })
  }

  const handleCancel = useCallback(
    (isDirty = false) => {
      if (isAdmin) {
        const path = projectId
          ? adminViewProjectPath({ routeParams: { projectId } })
          : adminListProjectsPath

        router.push(path)
      } else if (isDirty) {
        openModal(ModalType.SaveAsDraft, {
          prompt:
            'You have unsaved information for your planting project. What would you like to do?',
          destination: listProjectsPath,
          onSaveAsDraft: handleSaveAsDraft,
          onCancel: handleDiscard
        })
      } else {
        router.push(listProjectsPath)
      }
    },
    [handleSaveAsDraft, isAdmin, openModal, projectId, router]
  )

  useEffect(() => {
    if (isAdmin) return

    setFocussedHeader(handleCancel)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin])

  return (
    <GoogleMapsProvider>
      <Formik
        enableReinitialize
        initialValues={initialValues}
        onSubmit={handleSubmit}
        validationSchema={validationSchema}
        validateOnMount
      >
        {({ isValid, values, isSubmitting: isFormSubmitting, dirty }) => {
          if (!isValid && isFormSubmitting) {
            notify({
              id: 'projectFormErrorNotify',
              type: NotificationType.error,
              message:
                'You have one or more mandatory fields not filled in or checked.'
            })
          }
          const onCancel = () => {
            handleCancel(dirty)
          }
          return (
            <>
              <ProjectForm
                isAdmin={isAdmin}
                isSubmitting={isSubmitting}
                onSaveAsDraft={handleSaveAsDraft}
                onCancel={onCancel}
              />
              <UploadProgress
                title='Saving your project'
                content={
                  <dl>
                    <DefinitionListItem
                      title='Project name'
                      data={values.Name}
                    />
                  </dl>
                }
                isUploading={isUploading}
                filesComplete={progress.filesComplete}
                filesCount={progress.filesCount}
                percentageComplete={progress.percentageComplete}
              />
            </>
          )
        }}
      </Formik>
    </GoogleMapsProvider>
  )
}
